/**
 * Supervisor → Tenant Administrative Panel
 *
 * Centralised administrative surface accessible from the Supervisor
 * Panel for a specific tenant. Hosts the features that used to live
 * inside the tenant-facing «Διαχείριση» menu:
 *
 *   1. Ενεργά Modules
 *   2. Backup   (also kept inside the tenant app)
 *   3. Εισαγωγή / Εξαγωγή Πινάκων
 *   4. Έλεγχος Υγείας Δεδομένων  (also kept inside the tenant app)
 *   5. Διαχείριση Πινάκων (bulk delete / truncate)
 *
 * Implementation strategy — Impersonation:
 *   The Supervisor asks the backend for a short-lived tenant JWT
 *   (super_manager role, tenant_id bound). We stash it in
 *   ``localStorage.auth_token`` for the lifetime of this page so the
 *   shared `http` client transparently reaches the tenant DB through
 *   the middleware. On unmount / exit we restore whatever was there
 *   before so the supervisor's own session (``supervisor_token``) is
 *   never affected.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { supervisorApi } from "@/lib/SupervisorApi";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import BackupPanel from "@/components/BackupPanel";
import ImportExportPanel from "@/components/ImportExportPanel";
import JSZip from "jszip";
import DbHealthDialog from "@/components/DbHealthDialog";
import TableManagementPanel from "@/components/TableManagementPanel";
import PrintTemplatesPanel from "@/components/PrintTemplatesPanel";
import OperatorsTab from "@/components/OperatorsTab";
import { confirmDialog } from "@/lib/confirmDialog";
import {
  ArrowLeft,
  ShoppingCart,
  Utensils,
  Truck,
  ShieldCheck,
  Loader2,
  Building2,
  Download,
  Upload,
  Users,
  BarChart3,
  Boxes,
  QrCode,
  ConciergeBell,
} from "lucide-react";

export default function SupervisorTenantAdmin() {
  const { tenantCode } = useParams();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [modules, setModules] = useState({ retail: true, rest: true, delivery: false, customers: true, stats: true, stock: true, qrmenu: false, serving: true });
  const [settingsDoc, setSettingsDoc] = useState(null);
  const [modulesBusy, setModulesBusy] = useState(false);
  const [healthOpen, setHealthOpen] = useState(false);
  const [impUser, setImpUser] = useState(null);
  const [adminCode, setAdminCode] = useState("");
  const [adminCodeBusy, setAdminCodeBusy] = useState(false);

  // Set up impersonation tokens on mount, restore on unmount.
  useEffect(() => {
    // Verify supervisor token is present; else bounce.
    if (!localStorage.getItem("supervisor_token")) {
      navigate("/supervisor/login", { replace: true });
      return undefined;
    }

    const prevToken = localStorage.getItem("auth_token");
    const prevUser = localStorage.getItem("auth_user");

    (async () => {
      try {
        const [imp, info] = await Promise.all([
          supervisorApi.impersonate(tenantCode),
          supervisorApi.getTenant(tenantCode),
        ]);
        localStorage.setItem("auth_token", imp.data.token);
        localStorage.setItem("auth_user", JSON.stringify(imp.data.user));
        setImpUser(imp.data.user);
        setTenant(info.data);
        // Load current module toggle state from tenant settings.
        try {
          const r = await http.get("/settings");
          setSettingsDoc(r.data);
          setAdminCode(r.data.admin_code || "");
          setModules({
            retail: r.data.modules_retail !== false,
            rest: r.data.modules_rest !== false,
            delivery: r.data.modules_delivery === true,
            customers: r.data.modules_delivery === true || r.data.modules_customers !== false,
            stats: r.data.modules_stats !== false,
            stock: r.data.modules_stock !== false,
            qrmenu: r.data.modules_qrmenu === true,
            serving: r.data.modules_serving !== false,
          });
        } catch {
          /* non-blocking */
        }
        setReady(true);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Αποτυχία εισόδου στη διαχείριση πελάτη");
        navigate("/supervisor", { replace: true });
      }
    })();

    return () => {
      // Restore whatever token was there before (usually nothing for
      // a supervisor session). Never leak the impersonation token.
      if (prevToken) localStorage.setItem("auth_token", prevToken);
      else localStorage.removeItem("auth_token");
      if (prevUser) localStorage.setItem("auth_user", prevUser);
      else localStorage.removeItem("auth_user");
    };
  }, [tenantCode, navigate]);

  const toggleModule = async (key, val) => {
    const next = { ...modules, [key]: val };
    // Κανόνας: Πελάτες υποχρεωτικό όταν είναι ενεργό το Delivery.
    if (key === "delivery" && val) next.customers = true;
    if (key === "customers" && !val && modules.delivery) {
      toast.error("Το module «Πελάτες» είναι υποχρεωτικό όσο το Delivery είναι ενεργό");
      return;
    }
    setModules(next);
    setModulesBusy(true);
    try {
      // Merge πάνω στο πλήρες settings doc — αποφεύγει reset άλλων
      // ρυθμίσεων (το PUT /settings αντικαθιστά ολόκληρο το singleton).
      const base = settingsDoc || (await http.get("/settings")).data || {};
      const merged = {
        ...base,
        modules_retail: next.retail,
        modules_rest: next.rest,
        modules_delivery: next.delivery,
        modules_customers: next.customers,
        modules_stats: next.stats,
        modules_stock: next.stock,
        modules_qrmenu: next.qrmenu,
        modules_serving: next.serving,
      };
      await http.put("/settings", merged);
      setSettingsDoc(merged);
      toast.success("Αποθηκεύτηκε");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία");
      setModules(modules);
    } finally {
      setModulesBusy(false);
    }
  };

  const saveAdminCode = async () => {
    setAdminCodeBusy(true);
    try {
      const base = settingsDoc || (await http.get("/settings")).data || {};
      const merged = { ...base, admin_code: (adminCode || "").trim() };
      await http.put("/settings", merged);
      setSettingsDoc(merged);
      toast.success("Ο κωδικός διαχειριστή αποθηκεύτηκε");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία αποθήκευσης");
    } finally {
      setAdminCodeBusy(false);
    }
  };

  if (!ready) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-950 text-zinc-500">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={32} className="animate-spin" />
          <div className="text-sm">Είσοδος στη διαχείριση πελάτη «{tenantCode}»…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-zinc-950 text-zinc-100">
      {/* Top bar */}
      <div className="shrink-0 border-b border-zinc-800 bg-zinc-950/95">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 size={22} className="text-amber-400" />
            <div>
              <div className="font-heading font-bold text-lg leading-none">
                Διαχείριση Πελάτη —{" "}
                <span className="font-mono text-amber-400">{tenant?.tenant_code}</span>
              </div>
              <div className="text-[11px] text-zinc-500 uppercase tracking-wider font-bold">
                {tenant?.business_name}
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate("/supervisor")}
            data-testid="sup-tadmin-back"
            className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm font-bold flex items-center gap-1.5"
          >
            <ArrowLeft size={14} /> Επιστροφή στο Supervisor
          </button>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto scroll-thin">
        <div className="max-w-6xl mx-auto px-6 py-6 space-y-4 bg-white text-slate-900 rounded-lg my-4">
          {/* 1. Ενεργά Modules */}
          <Card className="p-6 space-y-3" data-testid="sup-tadmin-modules-card">
            <h3 className="font-heading text-lg font-semibold">Ενεργά Modules</h3>
            <p className="text-xs text-slate-500">
              Ενεργοποίηση/απενεργοποίηση των βασικών modules στη πλαϊνή μπάρα του πελάτη.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
              <ModuleToggle
                icon={ShoppingCart}
                label="Ταμείο (Retail)"
                checked={modules.retail}
                disabled={modulesBusy}
                onChange={(v) => toggleModule("retail", v)}
                testid="sup-tadmin-module-retail"
              />
              <ModuleToggle
                icon={Utensils}
                label="Εστιατόριο"
                checked={modules.rest}
                disabled={modulesBusy}
                onChange={(v) => toggleModule("rest", v)}
                testid="sup-tadmin-module-rest"
              />
              <ModuleToggle
                icon={Truck}
                label="Διανομές"
                checked={modules.delivery}
                disabled={modulesBusy}
                onChange={(v) => toggleModule("delivery", v)}
                testid="sup-tadmin-module-delivery"
              />
              <ModuleToggle
                icon={Users}
                label={modules.delivery ? "Πελάτες (υποχρεωτικό με Delivery)" : "Πελάτες"}
                checked={modules.customers}
                disabled={modulesBusy || modules.delivery}
                onChange={(v) => toggleModule("customers", v)}
                testid="sup-tadmin-module-customers"
              />
              <ModuleToggle
                icon={BarChart3}
                label="Στατιστικά"
                checked={modules.stats}
                disabled={modulesBusy}
                onChange={(v) => toggleModule("stats", v)}
                testid="sup-tadmin-module-stats"
              />
              <ModuleToggle
                icon={Boxes}
                label="Απόθεμα (Έλεγχος & Απογραφή)"
                checked={modules.stock}
                disabled={modulesBusy}
                onChange={(v) => toggleModule("stock", v)}
                testid="sup-tadmin-module-stock"
              />
              <ModuleToggle
                icon={QrCode}
                label="QR Menu (Τιμοκατάλογος)"
                checked={modules.qrmenu}
                disabled={modulesBusy}
                onChange={(v) => toggleModule("qrmenu", v)}
                testid="sup-tadmin-module-qrmenu"
              />
              <ModuleToggle
                icon={ConciergeBell}
                label="Σερβίρισμα (Runner)"
                checked={modules.serving}
                disabled={modulesBusy}
                onChange={(v) => toggleModule("serving", v)}
                testid="sup-tadmin-module-serving"
              />
            </div>
          </Card>

          {/* 1.5 Χειριστές & Κωδικοί (PIN) — full operator management */}
          <Card className="p-6 space-y-3" data-testid="sup-tadmin-operators-card">
            <h3 className="font-heading text-lg font-semibold">Χειριστές & Κωδικοί</h3>
            <p className="text-xs text-slate-500">
              Πλήρης διαχείριση χειριστών του πελάτη: δημιουργία, ορισμός PIN, ρόλος/όνομα, δικαιώματα, απενεργοποίηση, διαγραφή.
            </p>
            {impUser && <OperatorsTab currentUser={impUser} />}
          </Card>

          {/* 1.6 Κωδικός Διαχειριστή (ευαίσθητες ενέργειες) */}
          <Card className="p-6 space-y-3" data-testid="sup-tadmin-admincode-card">
            <h3 className="font-heading text-lg font-semibold">Κωδικός Διαχειριστή</h3>
            <p className="text-xs text-slate-500">
              Απαιτείται για ευαίσθητες ενέργειες του πελάτη: <strong>Force Reset</strong>,
              Backup/Επαναφορά, Εισαγωγή/Εξαγωγή Πινάκων, Ανανέωση QR. Το <strong>ΚΛΕΙΣΙΜΟ ΜΑΓΑΖΙΟΥ</strong> γίνεται
              πλέον με τον κωδικό (PIN) manager. Άφησέ τον κενό για να απενεργοποιήσεις τελείως αυτές τις ενέργειες.
            </p>
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1">Κωδικός</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  value={adminCode}
                  maxLength={20}
                  onChange={(e) => setAdminCode(e.target.value.replace(/\s/g, ""))}
                  placeholder="π.χ. 26502650"
                  className="h-12 w-56 text-2xl font-mono tracking-widest text-center rounded-md border-2 border-slate-300 focus:border-blue-500 outline-none text-slate-900"
                  data-testid="sup-tadmin-admin-code-input"
                />
              </div>
              <Button
                onClick={saveAdminCode}
                disabled={adminCodeBusy}
                className="bg-blue-600 hover:bg-blue-700 h-12 font-bold"
                data-testid="sup-tadmin-admin-code-save"
              >
                {adminCodeBusy ? <Loader2 size={16} className="animate-spin mr-1.5" /> : null}
                Αποθήκευση Κωδικού
              </Button>
            </div>
          </Card>

          {/* 2. Backup */}
          <BackupPanel supervisorMode />

          {/* 3. Import / Export */}
          <ImportExportPanel supervisorMode />

          {/* 3.5 Tenant Migration (Export / Import whole tenant) */}
          <TenantMigrationPanel tenantCode={tenantCode} />

          {/* 4. Health Check */}
          <Card className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <ShieldCheck size={18} /> Έλεγχος Υγείας Δεδομένων
                </h2>
                <p className="text-sm text-slate-600">
                  Σαρώνει όλους τους πίνακες και εντοπίζει εγγραφές με ελλιπή υποχρεωτικά
                  πεδία (orphan rows, αποτυχημένα imports κλπ).
                </p>
              </div>
              <Button
                onClick={() => setHealthOpen(true)}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="sup-tadmin-db-health-btn"
              >
                <ShieldCheck size={16} className="mr-1.5" /> Έλεγχος
              </Button>
            </div>
          </Card>

          {/* 5. Διαχείριση Πινάκων */}
          <TableManagementPanel />

          {/* 6. Πρότυπα Εκτυπώσεων (per-tenant override) */}
          <PrintTemplatesPanel scope="tenant" />
        </div>
      </div>

      {healthOpen && (
        <DbHealthDialog open={healthOpen} onClose={() => setHealthOpen(false)} />
      )}
    </div>
  );
}

function ModuleToggle({ icon: Icon, label, checked, disabled, onChange, testid }) {
  return (
    <label
      className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
        checked
          ? "border-emerald-400 bg-emerald-50"
          : "border-slate-200 bg-white hover:border-slate-300"
      } ${disabled ? "opacity-60 pointer-events-none" : ""}`}
    >
      <div
        className={`w-10 h-10 rounded-md grid place-items-center ${
          checked ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-500"
        }`}
      >
        <Icon size={20} />
      </div>
      <div className="flex-1 font-bold text-sm">{label}</div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </label>
  );
}

/**
 * Whole-tenant Export / Import
 *
 * Feb 2026 spec — lets the Supervisor download the full DB dump of a
 * tenant as JSON and re-upload it into another (or the same) tenant.
 * Handy for staging→production migration or cloning a chain's setup
 * to a new store. The import path requires the Supervisor's own
 * password as a safety net (destructive — wipes target's collections).
 */
function TenantMigrationPanel({ tenantCode }) {
  const [busy, setBusy] = useState(false);
  const [pwd, setPwd] = useState("");
  const [file, setFile] = useState(null);
  // Preview of the uploaded file — { name: count } — used to populate
  // the selective-import checkboxes. Parsed in-browser (no server
  // round-trip) so the operator sees choices before committing.
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState({});

  // Greek labels for the most common collections. Anything not in the
  // map falls back to the raw name so custom collections still show.
  const COLLECTION_LABELS = {
    products: "Προϊόντα",
    categories: "Κατηγορίες",
    modifier_groups: "Ομάδες Modifiers",
    modifiers: "Modifiers",
    departments: "Τμήματα ΦΠΑ",
    payments: "Τρόποι Πληρωμής",
    stations: "Σταθμοί",
    tables: "Τραπέζια",
    users: "Χρήστες",
    settings: "Ρυθμίσεις Επιχείρησης",
    receipts: "Αποδείξεις",
    shifts: "Βάρδιες",
    customer_purchases: "Ιστορικό Πελατών",
    print_templates: "Πρότυπα Εκτυπώσεων",
    printers: "Εκτυπωτές",
    logs: "Logs",
    audit_events: "Audit trail",
    counters: "Counters",
    login_attempts: "Login attempts",
    fiscal_jobs: "Fiscal jobs",
  };

  const onFileChange = async (f) => {
    setFile(f);
    setPreview(null);
    setSelected({});
    if (!f) return;
    try {
      let counts = {};
      if (f.name.toLowerCase().endsWith(".zip")) {
        const zip = await JSZip.loadAsync(await f.arrayBuffer());
        const mf = zip.file("manifest.json");
        if (!mf) throw new Error("missing manifest");
        const manifest = JSON.parse(await mf.async("string"));
        for (const [name, c] of Object.entries(manifest.tables || {})) {
          if (c > 0) counts[name] = c;
        }
      } else {
        const text = await f.text();
        const json = JSON.parse(text);
        const tables = json?.tables || {};
        for (const [name, docs] of Object.entries(tables)) {
          if (Array.isArray(docs) && docs.length > 0) counts[name] = docs.length;
        }
      }
      setPreview(counts);
      // Default selection: everything EXCEPT destructive/tenant-specific
      // collections (users, settings, receipts, shifts, audit trail).
      // The operator can always tick them back on.
      const SAFE_OFF = new Set([
        "users", "settings", "receipts", "shifts", "customer_purchases",
        "audit_events", "logs", "login_attempts", "counters", "fiscal_jobs",
      ]);
      const init = {};
      for (const name of Object.keys(counts)) {
        init[name] = !SAFE_OFF.has(name);
      }
      setSelected(init);
    } catch (e) {
      toast.error("Μη έγκυρο JSON αρχείο εξαγωγής");
    }
  };

  const doExport = async () => {
    setBusy(true);
    try {
      const r = await supervisorApi.exportTenant(tenantCode);
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 15);
      a.download = `tenant-${tenantCode}-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Το αρχείο εξαγωγής κατέβηκε.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία εξαγωγής");
    } finally {
      setBusy(false);
    }
  };

  const doImport = async () => {
    if (!file) {
      toast.error("Επίλεξε αρχείο ZIP ή JSON πρώτα.");
      return;
    }
    if (!pwd) {
      toast.error("Δώσε τον κωδικό διαχειριστή.");
      return;
    }
    const chosen = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (chosen.length === 0) {
      toast.error("Επίλεξε τουλάχιστον μία κατηγορία δεδομένων.");
      return;
    }
    const totalRows = chosen.reduce((s, k) => s + (preview?.[k] || 0), 0);
    if (!(await confirmDialog(
      `Θα ΑΝΤΙΚΑΤΑΣΤΑΘΟΥΝ ${chosen.length} κατηγορίες δεδομένων (${totalRows} εγγραφές) του tenant «${tenantCode}» από το «${file.name}». Είσαι σίγουρος;`,
    ))) return;
    setBusy(true);
    try {
      const r = await supervisorApi.importTenant(tenantCode, file, pwd, chosen);
      toast.success(
        `Επιτυχής εισαγωγή: ${Object.values(r.data.restored || {}).reduce((a, b) => a + b, 0)} εγγραφές σε ${Object.keys(r.data.restored || {}).length} κατηγορίες.`,
      );
      setFile(null);
      setPwd("");
      setPreview(null);
      setSelected({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία εισαγωγής");
    } finally {
      setBusy(false);
    }
  };

  const toggleAll = (val) => {
    const next = {};
    for (const k of Object.keys(preview || {})) next[k] = val;
    setSelected(next);
  };

  return (
    <Card className="p-4">
      <h2 className="text-lg font-bold flex items-center gap-2 mb-1">
        <Download size={18} /> Εξαγωγή / Εισαγωγή Πελάτη
      </h2>
      <p className="text-sm text-slate-600 mb-3">
        Ολόκληρος ο πελάτης ως ενιαίο αρχείο ZIP (χωρίς όριο μεγέθους).
        Στην εισαγωγή μπορείς να διαλέξεις ποιες κατηγορίες θα
        αντικατασταθούν, ώστε να μη χαθούν χρήστες / αποδείξεις /
        ρυθμίσεις του target.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
          <div className="font-bold text-sm mb-2 flex items-center gap-1.5">
            <Download size={14} /> Εξαγωγή
          </div>
          <div className="text-xs text-slate-600 mb-2">
            Κατεβαίνει το πλήρες dump του «{tenantCode}».
          </div>
          <Button
            onClick={doExport}
            disabled={busy}
            className="w-full bg-blue-600 hover:bg-blue-700"
            data-testid="sup-tadmin-export-tenant-btn"
          >
            {busy ? <Loader2 size={16} className="animate-spin mr-1.5" /> : <Download size={16} className="mr-1.5" />}
            Εξαγωγή ZIP
          </Button>
        </div>
        <div className="border border-red-200 rounded-lg p-3 bg-red-50">
          <div className="font-bold text-sm mb-2 flex items-center gap-1.5 text-red-800">
            <Upload size={14} /> Επιλεκτική Εισαγωγή
          </div>
          <input
            type="file"
            accept="application/zip,.zip,application/json,.json"
            onChange={(e) => onFileChange(e.target.files?.[0] || null)}
            className="text-xs mb-2 w-full"
            data-testid="sup-tadmin-import-tenant-file"
          />

          {preview && Object.keys(preview).length > 0 && (
            <div className="mb-2 bg-white border border-slate-200 rounded p-2 max-h-56 overflow-auto">
              <div className="flex items-center justify-between mb-1 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                <span>Επίλεξε κατηγορίες</span>
                <div className="flex gap-2">
                  <button type="button" onClick={() => toggleAll(true)}
                    className="text-blue-600 hover:underline"
                    data-testid="sup-tadmin-import-select-all">
                    όλα
                  </button>
                  <button type="button" onClick={() => toggleAll(false)}
                    className="text-blue-600 hover:underline"
                    data-testid="sup-tadmin-import-select-none">
                    κανένα
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-0.5">
                {Object.entries(preview)
                  .sort((a, b) => a[0].localeCompare(b[0]))
                  .map(([name, count]) => (
                    <label key={name}
                      className="flex items-center gap-2 text-xs py-0.5 px-1 rounded hover:bg-slate-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!selected[name]}
                        onChange={(e) => setSelected({ ...selected, [name]: e.target.checked })}
                        data-testid={`sup-tadmin-import-collection-${name}`}
                        className="accent-red-600"
                      />
                      <span className="font-mono flex-1">
                        {COLLECTION_LABELS[name] || name}
                      </span>
                      <span className="text-slate-500">{count}</span>
                    </label>
                  ))}
              </div>
            </div>
          )}

          <input
            type="password"
            placeholder="Ο δικός σου κωδικός Supervisor"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            className="text-xs mb-1 w-full h-8 px-2 rounded border border-slate-300"
            data-testid="sup-tadmin-import-tenant-password"
            autoComplete="current-password"
          />
          <div className="text-[10px] text-slate-500 mb-2 leading-tight px-0.5">
            Είναι ο κωδικός που χρησιμοποιείς για να συνδεθείς στον Supervisor —
            <strong> όχι</strong> ο PIN του καταστήματος.
          </div>
          <Button
            onClick={doImport}
            disabled={busy || !file || !pwd || !preview}
            className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-40"
            data-testid="sup-tadmin-import-tenant-btn"
          >
            {busy ? <Loader2 size={16} className="animate-spin mr-1.5" /> : <Upload size={16} className="mr-1.5" />}
            Αντικατάσταση Επιλεγμένων
          </Button>
        </div>
      </div>
    </Card>
  );
}
